import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Calculator, Plus, Search as SearchIcon, SlidersHorizontal } from 'lucide-react';
import type { OrderStatus } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';
import { formatDateShort, formatMinor, formatWhen } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useOrders } from '@/api/hooks/orders';
import { usePartners } from '@/api/hooks/catalog';
import type { OrderFilters } from '@/api/types';
import { Badge, StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox, Input, Select } from '@/components/ui/Field';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import { CardList, CardListEmpty, NarrowOnly, WideOnly } from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';

/** Групи, по които диспечерът реално филтрира — не списък от десет отметки. */
const QUICK_GROUPS: Array<{ key: string; label: string; statuses: OrderStatus[] }> = [
  { key: 'active', label: 'Активни', statuses: ['confirmed', 'planned', 'assigned', 'loaded', 'in_progress'] },
  { key: 'awaiting', label: 'Чакат бус', statuses: ['confirmed'] },
  { key: 'docs', label: 'Чакат документи', statuses: ['delivered', 'documents_pending'] },
  { key: 'billing', label: 'За фактуриране', statuses: ['ready_to_invoice'] },
  { key: 'closed', label: 'Приключени', statuses: ['invoiced', 'paid', 'closed'] },
];

export function OrdersPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { q?: string; group?: string };

  const [term, setTerm] = useState(search.q ?? '');
  const [group, setGroup] = useState<string>(search.group ?? 'active');
  const [partnerId, setPartnerId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [hardOnly, setHardOnly] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const partners = usePartners();

  const filters: OrderFilters = useMemo(() => {
    const statuses = QUICK_GROUPS.find((g) => g.key === group)?.statuses;
    const value: OrderFilters = { page, perPage: 50 };
    if (statuses) value.status = statuses;
    if (term.trim()) value.q = term.trim();
    if (partnerId) value.partnerId = partnerId;
    if (fromDate) value.fromDate = fromDate;
    if (toDate) value.toDate = toDate;
    if (hardOnly) value.hardDeadlineOnly = true;
    if (unassignedOnly) value.unassignedOnly = true;
    return value;
  }, [group, term, partnerId, fromDate, toDate, hardOnly, unassignedOnly, page]);

  const { data, isLoading, isError, refetch } = useOrders(filters);
  const items = data?.items ?? [];

  useHotkeys([
    {
      keys: 'n',
      description: t.orders.newOrder,
      group: 'Заявки',
      run: () => void navigate({ to: '/orders/new' }),
    },
  ]);

  const resetFilters = () => {
    setTerm('');
    setGroup('active');
    setPartnerId('');
    setFromDate('');
    setToDate('');
    setHardOnly(false);
    setUnassignedOnly(false);
    setPage(1);
    void navigate({ to: '/orders', search: {} });
  };

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.orders.title}</h1>
        <span className="text-xs text-ink-dim">
          {data ? `${data.total} ${t.common.results}` : ''}
        </span>
        <Link to="/quote" className="ml-auto">
          <Button icon={<Calculator className="size-4" />}>Изчисли оферта</Button>
        </Link>
        <Link to="/orders/new">
          <Button variant="primary" icon={<Plus className="size-4" />}>
            {t.orders.newOrder}
          </Button>
        </Link>
      </div>

      <Card className="p-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
            <Input
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                setPage(1);
              }}
              placeholder="Номер, номер на клиента, град, товар…"
              className="pl-8"
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              aria-label={t.common.search}
            />
          </div>
          {/* На телефон филтрите заемат цял екран — стоят прибрани, докато не потрябват. */}
          <Button
            className="shrink-0 board:hidden"
            variant="secondary"
            icon={<SlidersHorizontal className="size-4" />}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((value) => !value)}
          >
            {t.common.filters}
          </Button>
        </div>

        <div
          className={cn(
            'mt-3 flex-wrap items-end gap-3 board:flex',
            filtersOpen ? 'flex' : 'hidden',
          )}
        >
          <Select
            aria-label={t.common.status}
            className="w-full sm:w-44"
            value={group}
            onChange={(e) => {
              setGroup(e.target.value);
              setPage(1);
            }}
            options={[
              ...QUICK_GROUPS.map((g) => ({ value: g.key, label: g.label })),
              { value: '', label: t.common.all },
            ]}
          />

          <Select
            aria-label={t.orders.partner}
            className="w-full sm:w-52"
            value={partnerId}
            onChange={(e) => {
              setPartnerId(e.target.value);
              setPage(1);
            }}
            placeholder={`${t.orders.partner}: ${t.common.all}`}
            options={(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />

          <Input
            type="date"
            className="w-[calc(50%-0.375rem)] sm:w-36"
            aria-label={t.common.from}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <Input
            type="date"
            className="w-[calc(50%-0.375rem)] sm:w-36"
            aria-label={t.common.to}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />

          <Checkbox
            label="Само твърд срок"
            checked={hardOnly}
            onChange={(e) => setHardOnly(e.target.checked)}
          />
          <Checkbox
            label="Само без бус"
            checked={unassignedOnly}
            onChange={(e) => setUnassignedOnly(e.target.checked)}
          />

          <Button variant="ghost" size="sm" onClick={resetFilters}>
            {t.common.clearFilters}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <>
            {/* Тесен екран: седемте колони стават карта, която цялата е връзка. */}
            <NarrowOnly>
              {items.length === 0 ? (
                <CardListEmpty>{t.orders.noOrders}</CardListEmpty>
              ) : (
                <CardList>
                  {items.map((order) => (
                    <li key={order.id}>
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.id }}
                        data-tap
                        className="block px-3 py-3"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-sm text-accent">{order.number}</span>
                          <StatusPill status={order.status} />
                          <span className="ml-auto shrink-0 font-mono text-sm tabular">
                            {formatMinor(order.agreedPriceMinor + order.surchargesMinor, order.ccy)}
                          </span>
                        </div>
                        {order.customerRef ? (
                          <div className="text-[11px] text-ink-faint">реф. {order.customerRef}</div>
                        ) : null}
                        <div className="mt-1 truncate text-sm text-ink-dim">
                          {order.partnerName}
                        </div>
                        <div className="mt-0.5 text-sm">
                          {routeText(order)}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-dim">
                          <span className={order.isHardDeadline ? 'text-warn' : undefined}>
                            {t.orders.deadline}: {formatWhen(order.deadlineAt)}
                          </span>
                          {order.isHardDeadline ? <Badge tone="warn">твърд</Badge> : null}
                          {order.tripNumber ? (
                            <span className="font-mono">
                              {order.tripNumber}
                              {order.vehicleFleetNo ? ` · ${order.vehicleFleetNo}` : ''}
                              {order.driverName ? ` · ${order.driverName}` : ''}
                            </span>
                          ) : (
                            <span className="text-warn">без бус</span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </CardList>
              )}
            </NarrowOnly>

            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.orders.number}</TH>
                    <TH>{t.common.status}</TH>
                    <TH>{t.orders.partner}</TH>
                    <TH>{t.orders.route}</TH>
                    <TH>{t.orders.deadline}</TH>
                    <TH>{t.trips.number}</TH>
                    <TH numeric>{t.orders.price}</TH>
                  </TR>
                </THead>
                <TBody>
                  {items.length === 0 ? (
                    <TableEmpty colSpan={7}>{t.orders.noOrders}</TableEmpty>
                  ) : (
                    items.map((order) => (
                      <TR key={order.id} interactive>
                        <TD>
                          <Link
                            to="/orders/$orderId"
                            params={{ orderId: order.id }}
                            className="font-mono text-sm text-accent hover:underline"
                          >
                            {order.number}
                          </Link>
                          {order.customerRef ? (
                            <div className="text-[11px] text-ink-faint">
                              реф. {order.customerRef}
                            </div>
                          ) : null}
                        </TD>
                        <TD>
                          <StatusPill status={order.status} />
                        </TD>
                        <TD className="max-w-[14rem] truncate">{order.partnerName}</TD>
                        <TD className="whitespace-nowrap text-ink-dim">
                          {routeText(order)}
                        </TD>
                        <TD className="whitespace-nowrap">
                          {formatWhen(order.deadlineAt)}
                          {order.isHardDeadline ? (
                            <Badge tone="warn" className="ml-1">
                              твърд
                            </Badge>
                          ) : null}
                        </TD>
                        <TD className="whitespace-nowrap">
                          {order.tripId && order.tripNumber ? (
                            <Link
                              to="/trips/$tripId"
                              params={{ tripId: order.tripId }}
                              className="font-mono text-xs text-accent hover:underline"
                            >
                              {order.tripNumber}
                            </Link>
                          ) : (
                            <span className="text-xs text-ink-faint">—</span>
                          )}
                          {order.vehicleFleetNo ? (
                            <div className="text-[11px] text-ink-faint">
                              {order.vehicleFleetNo} · {order.driverName ?? ''}
                            </div>
                          ) : null}
                        </TD>
                        <TD numeric>
                          {formatMinor(order.agreedPriceMinor + order.surchargesMinor, order.ccy)}
                          <div className="text-[11px] text-ink-faint">
                            {formatDateShort(order.createdAt)}
                          </div>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </>
        )}
      </Card>

      {data && data.total > data.perPage ? (
        <div className="flex items-center justify-center gap-3 text-xs text-ink-dim">
          <Button
            size="sm"
            variant="ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t.common.back}
          </Button>
          <span>
            {t.common.page} {page} {t.common.of} {Math.ceil(data.total / data.perPage)}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={page >= Math.ceil(data.total / data.perPage)}
            onClick={() => setPage((p) => p + 1)}
          >
            {t.common.next}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** „Пловдив (BG) → Мюнхен (DE)"; без спирки — тире, не празни скоби. */
function routeText(order: { fromCity: string | null; fromCountry: string | null; toCity: string | null; toCountry: string | null }): string {
  const end = (city: string | null, country: string | null) =>
    city ? `${city}${country ? ` (${country})` : ''}` : country ?? '—';
  return `${end(order.fromCity, order.fromCountry)} → ${end(order.toCity, order.toCountry)}`;
}
